import { useState } from 'react';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Button from '@mui/material/Button';
import styled from '@mui/material/styles/styled';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import { useUser } from '../../../../contexts/UserContext';
import AlertMessage from '../../../elements/alert-message/AlertMessage';
import Text from '../../../elements/text/Text';
import Modal from '../../../sections/modal/Modal';
import ModalButtons from '../../../sections/modal/ModalButtons';

const StyledSpan = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderBottom: 'solid 2px black',
}));

const DeleteFilesButton = ({ action, loading, selectedFiles }) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState('');

  const handleFormChange = (e) => setForm(e.target.value);

  const toggleModal = () => {
    setOpen(!open);
    setForm('');
  };

  const handleDelete = () => {
    action();
    toggleModal();
  };

  const validation = user?.login;

  return (
    <>
      <Button
        startIcon={<DeleteForeverIcon />}
        onClick={toggleModal}
        size="small"
        variant="text"
        color="inherit"
        disabled={loading || selectedFiles.length === 0}
      >
        {t('buttons.delete', { ns: 'fileBrowser' })}
      </Button>
      <Modal
        openModal={open}
        title={t('deletion', {
          ns: 'project',
          text: t('file', { ns: 'fields' }),
        })}
        action={
          <ModalButtons
            actionProps={{ disabled: form !== validation }}
            actionText={t('delete', { ns: 'buttons' })}
            cancelProps={{ type: 'reset' }}
            danger
            onAction={handleDelete}
            onCancel={toggleModal}
          />
        }
        handleClose={toggleModal}
      >
        <AlertMessage
          severity="error"
          message={t('cantUndo', { ns: 'descriptions' })}
          noPadding
        />
        <Text sx={{ mt: 2 }}>
          {t('areYouSureFiles', { ns: 'descriptions' })}{' '}
          <Trans
            i18nKey="deleteValidation"
            ns="descriptions"
            values={{ validation }}
            components={[<StyledSpan key="0" />]}
            shouldUnescape={true}
          />
        </Text>
        <TextField
          autoComplete="off"
          fullWidth
          label={t('deleteConfirm', { ns: 'fields' })}
          onChange={handleFormChange}
          size="small"
          slotProps={{
            htmlInput: {
              'aria-label': 'delete confirmation',
            },
          }}
          sx={{ mt: 3 }}
          value={form}
          variant="outlined"
        />
      </Modal>
    </>
  );
};

DeleteFilesButton.propTypes = {
  action: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  selectedFiles: PropTypes.array.isRequired,
};

export default DeleteFilesButton;
